import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';

import { TripSubscriptionListRead } from 'src/common/external/bambi-api/bambiApi';
import { Avatar } from 'src/common/primitives/Avatar';
import { formatPassengerFullName } from 'src/common/util/formatFullName';
import { RowActions } from 'src/features/trip/trip-subscriptions/management/RowActions';

import { TripSubscriptionExpandedNameCell } from './TripSubscriptionExpandedNameCell';
import { TripSubscriptionExpandedSummaryCell } from './TripSubscriptionExpandedSummaryCell';
import { getWeekdays } from './utilities';

const columnHelper = createColumnHelper<TripSubscriptionListRead>();

const EmptyExpandedSection = function () {
  return <div></div>;
};

export const columns = [
  columnHelper.accessor('passenger', {
    enableSorting: true,
    id: 'name',
    header: 'Passenger',
    cell: (props) => {
      const { row } = props;
      return (
        <div className="h-fit">
          <div className="flex gap-x-1">
            <div>
              <Avatar
                firstName={row.original.passenger.first_name}
                lastName={row.original.passenger.last_name}
              />
            </div>
            <p className="w-full p-3 font-medium text-gray-900">
              {formatPassengerFullName(row.original.passenger)}
            </p>
          </div>

          {row.getCanExpand() && (
            <div>
              {row.getIsExpanded() && (
                <TripSubscriptionExpandedNameCell {...props} />
              )}
            </div>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('dispatcher_notes', {
    id: 'dispatcher_notes',
    header: 'Summary',
    cell: (props) => {
      const { row } = props;
      const recurrenceDays = getWeekdays(row.original.recurrences).filter(
        (element, index, self) => index === self.indexOf(element)
      );

      const showExpandedVersion = row.getCanExpand() && row.getIsExpanded();
      return (
        <div>
          {showExpandedVersion ? (
            <div className="">
              <p className="mb-3 text-sm text-gray-500">
                {row.original.dispatcher_notes}
              </p>
              <TripSubscriptionExpandedSummaryCell {...props} />
            </div>
          ) : (
            <div className="flex gap-x-2 whitespace-normal">
              <span className="w-[250px] text-ellipsis">
                {row.original.dispatcher_notes}
              </span>
              {recurrenceDays.map((weekday) => (
                <div key={weekday}>
                  <Badge label={weekday} color="gray" />
                </div>
              ))}
            </div>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('start_date', {
    enableSorting: true,
    id: 'start_date',
    header: 'Start Date',
    cell: ({ row }) => {
      return (
        <div>
          <div className="text-sm text-gray-500">{row.original.start_date}</div>
          {row.getCanExpand() && <EmptyExpandedSection />}
        </div>
      );
    },
  }),
  columnHelper.accessor('expires_at', {
    enableSorting: true,
    id: 'expires_at',
    header: 'End Date',
    cell: ({ row }) => {
      return (
        <div>
          <div className="text-sm text-gray-500">{row.original.expires_at}</div>
          {row.getCanExpand() && <EmptyExpandedSection />}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const isExpired =
        DateTime.fromISO(row.original.expires_at) <
        DateTime.now().startOf('day');

      const isExpiringSoon =
        DateTime.fromISO(row.original.expires_at) <=
        DateTime.now().plus({ days: 7 }).startOf('day');

      if (isExpired) {
        return <Badge label="Expired" color="red" />;
      } else if (isExpiringSoon) {
        return (
          <div className="relative inline-block">
            <Badge label="Expiring Soon" color="yellow" />
            <div className="absolute -right-0.5 -top-0.5 h-3 w-3 animate-pulse rounded-full bg-red-500" />
          </div>
        );
      }
      return <Badge label="Active" color="green" />;
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Manage',
    enableHiding: false,
    meta: {
      headerWidth: 80,
    },
    cell: (props) => (
      <div onClick={(e) => e.stopPropagation()}>
        <RowActions tripSubscription={props.row.original} />
      </div>
    ),
  }),
];
